/**
 * EP-160 section 2.3: the support session in the browser.
 *
 * The FlowRent support console opens `app.flowrent.io#support_session=<code>`
 * in a new tab. main.tsx strips the fragment before analytics and Sentry start,
 * then exchanges the one-time code for the session token before AuthProvider
 * renders. From then on this tab is a "support tab" for its whole life:
 *
 * - every request carries the session token (api.ts addAuthHeader), even when
 *   localStorage holds a user refresh token from an ordinary sign-in;
 * - nothing refreshes, and a 401 shows "เซสชันหมดอายุ" instead of /login;
 * - logout ends the session instead of revoking a user token;
 * - no client analytics (AuthProvider, analytics.ts).
 *
 * The token lives in module memory and in sessionStorage under
 * `flowrent_support_session` (one tab, survives a reload), never in the
 * refresh-token slot. After the session ends or expires the same key holds
 * `ended` or `expired`, so a reload shows the same screen instead of turning
 * the tab into an ordinary signed-in tab.
 *
 * Browser only: on the operator app (`isNativePlatform()`) every function here
 * is a no-op, so the app never reads the fragment and never runs a session.
 *
 * Uses the bare axios instance, not `api`: the exchange is public and must not
 * carry a user bearer, and api.ts imports this module.
 */
import axios from 'axios'
import { isNativePlatform } from '@/lib/platform'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export const SUPPORT_SESSION_STORAGE_KEY = 'flowrent_support_session'
const FRAGMENT_PARAM = 'support_session'

/** `support_session` on GET /api/session and on the exchange response. */
export interface SupportSessionInfo {
  id: number
  admin_name: string
  scope: 'read' | 'setup' | 'tenants'
  ends_at: string
  scope_label_key?: string
  property_ids?: number[] | null
}

/**
 * none: an ordinary tab. starting: a code was read and the exchange has not
 * finished. active: the token is in use. expired: a request answered 401.
 * ended: the session was ended from this tab. invalid_link: the exchange was
 * refused (unknown, used or expired code) or failed.
 */
export type SupportSessionStatus = 'none' | 'starting' | 'active' | 'expired' | 'ended' | 'invalid_link'

type FinishedStatus = 'expired' | 'ended'

interface ExchangeResponse {
  data: {
    access_token: string
    expires_at?: string
    session?: SupportSessionInfo
  }
}

let status: SupportSessionStatus = 'none'
let pendingCode: string | null = null
let token: string | null = null
let info: SupportSessionInfo | null = null
const listeners = new Set<() => void>()

function setStatus(next: SupportSessionStatus): void {
  if (status === next) return
  status = next
  listeners.forEach((listener) => listener())
}

function readStorage(): string | null {
  try {
    return sessionStorage.getItem(SUPPORT_SESSION_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStorage(value: string): void {
  try {
    sessionStorage.setItem(SUPPORT_SESSION_STORAGE_KEY, value)
  } catch {
    // Storage refused (private mode quota): the session still runs in memory.
  }
}

function finish(next: FinishedStatus): void {
  token = null
  info = null
  writeStorage(next)
  setStatus(next)
}

/**
 * Reads `#support_session=<code>` and removes it from the address bar with
 * history.replaceState (other fragment parameters stay). With no code, picks
 * up this tab's stored session (a reload). Must run before initAnalytics and
 * Sentry.init, so the code never reaches a captured URL. Returns the code.
 */
export function readAndStripFragment(): string | null {
  if (typeof window === 'undefined' || isNativePlatform()) return null

  const hash = window.location.hash
  if (hash.includes(`${FRAGMENT_PARAM}=`)) {
    const params = new URLSearchParams(hash.slice(1))
    const code = params.get(FRAGMENT_PARAM)
    params.delete(FRAGMENT_PARAM)
    const rest = params.toString()
    const { pathname, search } = window.location
    window.history.replaceState(window.history.state, '', `${pathname}${search}${rest ? `#${rest}` : ''}`)
    if (code) {
      pendingCode = code
      token = null
      setStatus('starting')
      return code
    }
  }

  const stored = readStorage()
  if (stored === 'expired' || stored === 'ended') {
    setStatus(stored)
  } else if (stored) {
    token = stored
    setStatus('active')
  }
  return null
}

/**
 * Exchanges the code readAndStripFragment found (POST
 * /api/support_sessions/exchange) and stores the session token. Resolves
 * either way; a refused or failed exchange leaves the tab on invalid_link.
 * No-op when there is no pending code.
 */
export async function bootstrapSupportSession(): Promise<void> {
  if (isNativePlatform() || status !== 'starting' || !pendingCode) return
  const code = pendingCode
  pendingCode = null
  try {
    const response = await axios.post<ExchangeResponse>(`${API_BASE_URL}/api/support_sessions/exchange`, { code })
    const sessionToken = response.data.data.access_token
    if (!sessionToken) throw new Error('exchange returned no token')
    token = sessionToken
    info = response.data.data.session ?? null
    writeStorage(sessionToken)
    setStatus('active')
  } catch {
    setStatus('invalid_link')
  }
}

/** True for the whole life of a support tab, including after it expired or ended. */
export function isSupportSession(): boolean {
  return status !== 'none'
}

export function getSupportSessionStatus(): SupportSessionStatus {
  return status
}

/** The session token while the session is active, else null. */
export function getSupportSessionToken(): string | null {
  return status === 'active' ? token : null
}

export function getSupportSessionInfo(): SupportSessionInfo | null {
  return info
}

/** AuthProvider stores what GET /api/session returned, for the banner. */
export function setSupportSessionInfo(next: SupportSessionInfo): void {
  if (status !== 'active') return
  info = next
  listeners.forEach((listener) => listener())
}

/** For useSyncExternalStore: called on every status or info change. */
export function subscribeSupportSession(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** api.ts: a request answered 401 while the session was active. */
export function markSupportSessionExpired(): void {
  if (status === 'active') finish('expired')
}

/**
 * Ends the session (DELETE /api/support_session with the session bearer) and
 * clears the token. The tab then shows "จบเซสชันแล้ว". A failed request still
 * clears the tab: the server ends the session at its cap or on the grant.
 */
export async function endSupportSession(): Promise<void> {
  if (status === 'none') return
  const current = getSupportSessionToken()
  if (current) {
    try {
      await axios.delete(`${API_BASE_URL}/api/support_session`, {
        headers: { Authorization: `Bearer ${current}` },
      })
    } catch {
      // Ignore: the tab is cleared below either way.
    }
  }
  if (status === 'active' || status === 'starting') finish('ended')
}
